import React from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

import type { ChangeValue } from '../results-step.types';

/**
 * `trend` — a movement against a baseline: the arrow carries the direction and
 * the tint is borderless (overview table).
 * `total` — a standalone rollup where the sign in the label is enough, rendered
 * with the design-system `badge/{tone}/filled` treatment (bordered, no arrow).
 */
export type ChangeBadgeVariant = 'trend' | 'total';

const VARIANT_STYLES: Record<
  ChangeBadgeVariant,
  Record<ChangeValue['direction'], string>
> = {
  trend: {
    up: 'bg-success-100 text-success-700',
    down: 'bg-critical-50 text-critical-700',
    neutral: 'bg-gray-100 text-gray-600',
  },
  total: {
    up: 'bg-success-100 text-success-800 border border-success-300',
    down: 'bg-critical-50 text-critical-700 border border-critical-200',
    neutral: 'bg-gray-100 text-gray-600 border border-gray-200',
  },
};

// Arrow from value sign; color from `direction` (invertBadge flips color only).
const resolveDirectionIcon = (
  change: ChangeValue
): typeof Icons.ArrowUp | null => {
  if (change.direction === 'neutral') return null;
  if (change.value > 0) return Icons.ArrowUp;
  if (change.value < 0) return Icons.ArrowDown;

  return null;
};

interface ChangeBadgeProps {
  change: ChangeValue | null;
  variant?: ChangeBadgeVariant;
  'data-testid'?: string;
}

const ChangeBadge = ({
  change,
  variant = 'trend',
  'data-testid': dataTestId,
}: ChangeBadgeProps): React.JSX.Element => {
  if (!change) {
    return <span data-testid={dataTestId} />;
  }

  const Icon = variant === 'trend' ? resolveDirectionIcon(change) : null;

  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold',
        VARIANT_STYLES[variant][change.direction]
      )}
      data-testid={dataTestId}
    >
      {Icon && <Icon customClass="size-3.5 shrink-0" />}
      {change.label}
    </span>
  );
};

export default ChangeBadge;
